import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import { useSubmitPublicationRequest } from "../../hooks/useSubmitPublicationRequest";
import type { PublicationRequestDto } from "../../api/publicationRequestsApi";

const EMPTY: PublicationRequestDto = {
  name: "",
  email: "",
  commune: "",
  description: "",
  proposedDate: "",
};

type Step = "ask" | "form";

export default function PublishRequestPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState<Step>("ask");
  const [form, setForm] = useState<PublicationRequestDto>(EMPTY);
  const submit = useSubmitPublicationRequest();
  const set = <K extends keyof PublicationRequestDto>(key: K, v: PublicationRequestDto[K]) =>
    setForm((prev) => ({ ...prev, [key]: v }));

  // Already signed in? Skip the gate: they land straight in the back-office creation flow.
  useEffect(() => {
    if (isAuthenticated) navigate("/admin/parcours/new", { replace: true });
  }, [isAuthenticated, navigate]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit.mutate({
      ...form,
      commune: form.commune || undefined,
      description: form.description || undefined,
      proposedDate: form.proposedDate || undefined,
    });
  };

  if (step === "ask") {
    return (
      <section className="container-w" style={{ padding: "80px 40px", maxWidth: 720 }}>
        <Link to="/" style={{ fontSize: 13, color: "var(--ink-mute)" }}>
          ← Retour à l'accueil
        </Link>

        <header style={{ marginTop: "var(--sp-5)", marginBottom: "var(--sp-7)" }}>
          <span style={{ fontFamily: "var(--font-script)", color: "var(--pink)", fontSize: 22 }}>
            Mozaika
          </span>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(40px, 6vw, 72px)",
              textTransform: "uppercase",
              lineHeight: 0.95,
              margin: "var(--sp-2) 0 var(--sp-4)",
            }}
          >
            Publier mon parcours
          </h1>
          <p style={{ color: "var(--ink-soft)", fontSize: 16, maxWidth: 560, margin: 0 }}>
            Avez-vous déjà un compte sur Mozaika ?
          </p>
        </header>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "var(--sp-4)",
          }}
        >
          <ChoiceCard
            title="Oui, j'ai un compte"
            body="Connectez-vous pour créer et publier votre parcours directement depuis votre espace."
            cta="Se connecter"
            onClick={() => navigate("/signin")}
            variant="primary"
          />
          <ChoiceCard
            title="Non, je n'en ai pas encore"
            body="Envoyez-nous une courte demande : un administrateur créera votre compte et vous recevrez vos accès par e-mail."
            cta="Remplir le formulaire"
            onClick={() => setStep("form")}
            variant="secondary"
          />
        </div>
      </section>
    );
  }

  if (submit.isSuccess) {
    return (
      <section className="container-w" style={{ padding: "80px 40px", maxWidth: 720 }}>
        <div
          style={{
            background: "var(--paper-warm)",
            border: "1.5px solid var(--ink)",
            borderRadius: "var(--radius-l)",
            padding: "var(--sp-8)",
            textAlign: "center",
          }}
        >
          <span style={{ fontFamily: "var(--font-script)", color: "var(--green-d)", fontSize: 28 }}>
            Merci !
          </span>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 44,
              textTransform: "uppercase",
              lineHeight: 0.95,
              margin: "var(--sp-3) 0 var(--sp-4)",
            }}
          >
            Demande envoyée
          </h1>
          <p style={{ color: "var(--ink-soft)", fontSize: 16, margin: 0 }}>
            Votre demande a bien été enregistrée. Un administrateur la traitera prochainement et vous recevrez un e-mail
            une fois votre compte créé.
          </p>
          <Link to="/" className="btn btn-primary" style={{ marginTop: "var(--sp-6)" }}>
            Retour à l'accueil
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="container-w" style={{ padding: "80px 40px", maxWidth: 720 }}>
      <button
        type="button"
        onClick={() => setStep("ask")}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          fontSize: 13,
          color: "var(--ink-mute)",
          cursor: "pointer",
          fontFamily: "var(--font-body)",
        }}
      >
        ← Retour
      </button>

      <header style={{ marginTop: "var(--sp-5)", marginBottom: "var(--sp-7)" }}>
        <span style={{ fontFamily: "var(--font-script)", color: "var(--pink)", fontSize: 22 }}>
          Mozaika
        </span>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(40px, 6vw, 72px)",
            textTransform: "uppercase",
            lineHeight: 0.95,
            margin: "var(--sp-2) 0 var(--sp-4)",
          }}
        >
          Publier mon parcours
        </h1>
        <p style={{ color: "var(--ink-soft)", fontSize: 16, maxWidth: 560, margin: 0 }}>
          Vous organisez un parcours d'artistes ? Remplissez ce formulaire ; un administrateur le validera et vous
          recevrez vos accès par e-mail pour le compléter en ligne.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        style={{
          background: "var(--paper-warm)",
          border: "1.5px solid var(--ink)",
          borderRadius: "var(--radius-l)",
          padding: "var(--sp-7)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--sp-5)",
        }}
      >
        <Field label="Nom de l'organisateur ou du parcours *">
          <input required value={form.name} onChange={(e) => set("name", e.target.value)} style={inputStyle} />
        </Field>
        <Field label="E-mail *">
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Commune principale">
          <input value={form.commune} onChange={(e) => set("commune", e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Date pressentie">
          <input
            type="date"
            value={form.proposedDate}
            onChange={(e) => set("proposedDate", e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Description du parcours">
          <textarea
            rows={6}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Édition, thème, nombre d'artistes, partenaires…"
            style={{ ...inputStyle, fontFamily: "var(--font-body)", resize: "vertical" }}
          />
        </Field>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--sp-3)" }}>
          <Link to="/" className="btn btn-light">
            Annuler
          </Link>
          <button type="submit" className="btn btn-primary" disabled={submit.isPending}>
            {submit.isPending ? "Envoi…" : "Envoyer la demande →"}
          </button>
        </div>
        {submit.isError && (
          <p style={{ color: "var(--red)", fontSize: 13, margin: 0 }}>
            Erreur : {(submit.error as Error)?.message ?? "envoi impossible"}
          </p>
        )}
      </form>
    </section>
  );
}

function ChoiceCard({
  title,
  body,
  cta,
  onClick,
  variant,
}: {
  title: string;
  body: string;
  cta: string;
  onClick: () => void;
  variant: "primary" | "secondary";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        background: variant === "primary" ? "var(--ink)" : "var(--paper-warm)",
        color: variant === "primary" ? "var(--paper)" : "var(--ink)",
        border: "1.5px solid var(--ink)",
        borderRadius: "var(--radius-l)",
        padding: "var(--sp-6)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--sp-3)",
        cursor: "pointer",
        fontFamily: "var(--font-body)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-display)",
          textTransform: "uppercase",
          fontSize: 20,
          lineHeight: 1.1,
        }}
      >
        {title}
      </span>
      <span
        style={{
          fontSize: 14,
          opacity: variant === "primary" ? 0.85 : 0.75,
          lineHeight: 1.5,
        }}
      >
        {body}
      </span>
      <span
        style={{
          marginTop: "var(--sp-3)",
          fontSize: 13,
          fontWeight: 700,
          textDecoration: "underline",
        }}
      >
        {cta} →
      </span>
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-soft)" }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "12px 16px",
  border: "1.5px solid var(--line)",
  borderRadius: "var(--radius-s)",
  background: "var(--paper)",
  fontSize: 14,
  fontFamily: "var(--font-body)",
  color: "var(--ink)",
};
